import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getClaudeTokens,
  createClaudeToken,
  updateClaudeToken,
  getClaudeTokenUsage,
  revokeClaudeToken,
  saveClaudeSettings,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime, fmtDateDMY } from '../utils/format';
import { Pill, PageHeading, Th, NtIcon, NT_COLORS } from '../components/NotionUI';
import Select from '../components/Select';

// Scadenze proposte (giorni da oggi); "0" = non scade mai.
const EXPIRY_CHOICES = ['30', '90', '365', '0'];

const EXPIRING_DAYS = 7;

/** Barre degli ultimi giorni di un token: richieste (e quante con errore). */
function UsageBars({ days, t }) {
  const max = Math.max(1, ...days.map((d) => d.requests));
  return (
    <div className="cl-usage" aria-label={t('claude.usageDaily')}>
      {days.map((d) => (
        <div key={d.day} className="cl-usage-col" title={t('claude.usageDay', { day: d.day.slice(8, 10) + '/' + d.day.slice(5, 7), requests: d.requests, errors: d.errors })}>
          <div className="cl-usage-bar" style={{ height: `${Math.max(d.requests > 0 ? 4 : 1, (d.requests / max) * 40)}px` }}>
            {d.errors > 0 && <div className="cl-usage-err" style={{ height: `${(d.errors / d.requests) * 100}%` }} />}
          </div>
          <span>{d.day.slice(8, 10)}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS_COLOR = { active: NT_COLORS.green, expired: NT_COLORS.yellow, revoked: NT_COLORS.red };

function ClaudeConnector({ user, onLogout }) {
  const { t } = useLanguage();
  const [tokens, setTokens] = useState([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [scope, setScope] = useState('mine');
  const [mcpUrl, setMcpUrl] = useState('');
  const [rateLimit, setRateLimit] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', days: '90' });
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [usageOpen, setUsageOpen] = useState({});

  const load = useCallback(async (nextScope) => {
    setLoading(true);
    try {
      const data = await getClaudeTokens(nextScope);
      setTokens(data.tokens || []);
      setCanManageAll(!!data.can_manage_all);
      setMcpUrl(data.mcp_url || '');
      setRateLimit(data.rate_limit_per_minute ? String(data.rate_limit_per_minute) : '');
    } catch (err) {
      setError(errorMessage(err, t('claude.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(scope);
  }, [scope, load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);
    try {
      const res = await createClaudeToken({
        name: form.name.trim(),
        expires_in_days: Number(form.days) || null,
      });
      setRevealed({ name: form.name.trim(), url: res.connector_url });
      setForm({ ...form, name: '' });
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.createError')));
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(revealed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError(t('claude.copyError'));
    }
  };

  const handleRevoke = async (token) => {
    const own = token.is_mine;
    const message = own
      ? t('claude.confirmRevoke', { name: token.name })
      : t('claude.confirmRevokeOther', { name: token.name, owner: `${token.owner_name} ${token.owner_surname}`.trim() });
    if (!window.confirm(message)) return;
    setError('');
    setSuccess('');
    try {
      await revokeClaudeToken(token.id);
      setSuccess(t('claude.revoked'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.revokeError')));
    }
  };

  const handleExpiry = async (token, days) => {
    if (days === '') return;
    setError('');
    setSuccess('');
    try {
      await updateClaudeToken(token.id, { expires_in_days: Number(days) || null });
      setSuccess(t('claude.expiryUpdated'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.expiryError')));
    }
  };

  const handleSaveLimit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingLimit(true);
    try {
      await saveClaudeSettings({ rate_limit_per_minute: rateLimit.trim() === '' ? null : Number(rateLimit) });
      setSuccess(t('claude.settingsSaved'));
    } catch (err) {
      setError(errorMessage(err, t('claude.settingsError')));
    } finally {
      setSavingLimit(false);
    }
  };

  const toggleUsage = async (token) => {
    if (usageOpen[token.id]) {
      setUsageOpen({ ...usageOpen, [token.id]: null });
      return;
    }
    try {
      const res = await getClaudeTokenUsage(token.id, 14);
      setUsageOpen({ ...usageOpen, [token.id]: res.days });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  // Token attivi che scadono a breve: avviso in cima alla pagina.
  const expiring = tokens.filter((tk) => tk.status === 'active' && tk.expires_at && (new Date(tk.expires_at.replace(' ', 'T')).getTime() - Date.now()) < EXPIRING_DAYS * 86400000);

  const expiryText = (token) => (token.expires_at ? fmtDateDMY(token.expires_at) : t('claude.never'));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="sparkle" title={t('claude.title')} subtitle={t('claude.subtitle')} />

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <section className="cl-card">
          <h3 className="cl-card-title">{t('claude.howTitle')}</h3>
          <ol className="cl-steps">
            <li>{t('claude.step1')}</li>
            <li>{t('claude.step2')}</li>
            <li>{t('claude.step3')}</li>
          </ol>
          <p className="tp-hint">{t('claude.howHint')}</p>
          {mcpUrl && <p className="tp-hint">{t('claude.baseUrl')} <code className="cl-code">{mcpUrl}/&lt;token&gt;</code></p>}
        </section>

        {expiring.length > 0 && (
          <div className="cl-warning" role="alert">
            <strong>{t('claude.expiringTitle')}</strong>
            <ul>
              {expiring.map((tk) => (
                <li key={tk.id}>{t('claude.expiringItem', { name: tk.name, owner: `${tk.owner_name} ${tk.owner_surname}`.trim(), date: fmtDateTime(tk.expires_at) })}</li>
              ))}
            </ul>
            <span>{t('claude.expiringHint')}</span>
          </div>
        )}

        {revealed && (
          <div className="au-secret">
            <div className="au-secret-head">
              <span className="au-secret-icon" aria-hidden="true"><NtIcon name="key" size={18} /></span>
              <div>
                <h3>{t('claude.revealTitle')} — {revealed.name}</h3>
                <p className="tp-hint">{t('claude.revealHint')}</p>
              </div>
            </div>
            <div className="au-secret-code">
              <code>{revealed.url}</code>
              <button className="btn btn-secondary btn-sm" type="button" onClick={copyUrl}>
                {copied ? t('claude.copied') : t('claude.copy')}
              </button>
            </div>
            <button className="btn" type="button" onClick={() => setRevealed(null)}>
              {t('claude.revealClose')}
            </button>
          </div>
        )}

        <section className="cl-card">
          <h3 className="cl-card-title">{t('claude.newTitle')}</h3>
          <form className="cl-new" onSubmit={handleCreate}>
            <div className="form-group cl-new-name">
              <label>{t('claude.tokenName')}</label>
              <input
                value={form.name}
                maxLength={100}
                placeholder={t('claude.tokenNamePlaceholder')}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group cl-new-expiry">
              <label>{t('claude.expiry')}</label>
              <Select value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}>
                {EXPIRY_CHOICES.map((d) => (
                  <option key={d} value={d}>{t(`claude.exp_${d}`)}</option>
                ))}
              </Select>
            </div>
            <button type="submit" className="btn" disabled={creating || form.name.trim() === ''}>
              {creating ? t('common.saving') : t('claude.create')}
            </button>
          </form>
          <p className="tp-hint">{t('claude.expiryHint')}</p>
        </section>

        <section className="cl-card">
          <div className="cl-list-head">
            <h3 className="cl-card-title">{scope === 'company' ? t('claude.companyTokens') : t('claude.myTokens')}</h3>
            {canManageAll && (
              <div className="tpl-cats" role="group" aria-label={t('claude.scope')}>
                <button type="button" className={'tpl-cat' + (scope === 'mine' ? ' tpl-cat-active' : '')} onClick={() => setScope('mine')}>
                  {t('claude.scopeMine')}
                </button>
                <button type="button" className={'tpl-cat' + (scope === 'company' ? ' tpl-cat-active' : '')} onClick={() => setScope('company')}>
                  {t('claude.scopeCompany')}
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : tokens.length === 0 ? (
            <p className="nt-empty">{t('claude.noTokens')}</p>
          ) : (
            <div className="nt-table-wrap">
              <table className="nt-table cu-table">
                <thead>
                  <tr>
                    <Th icon="key">{t('claude.colName')}</Th>
                    {scope === 'company' && <Th icon="user">{t('claude.colOwner')}</Th>}
                    <Th icon="status">{t('claude.colStatus')}</Th>
                    <Th icon="calendar">{t('claude.colExpires')}</Th>
                    <Th icon="clock">{t('claude.colLastUsed')}</Th>
                    <Th icon="hash">{t('claude.colUsage')}</Th>
                    <Th icon="more">{t('claude.colActions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.id} className={token.status === 'active' ? '' : 'cl-row-off'}>
                      <td>
                        <div className="cl-token-name">{token.name}</div>
                        <div className="nt-muted cl-token-prefix">{token.token_prefix}… · {t('claude.createdOn', { date: fmtDateDMY(token.created_at) })}</div>
                      </td>
                      {scope === 'company' && (
                        <td>
                          {`${token.owner_name} ${token.owner_surname}`.trim()}
                          {token.is_mine && <span className="nt-muted"> ({t('tickets.you')})</span>}
                        </td>
                      )}
                      <td>
                        <Pill dot color={STATUS_COLOR[token.status]}>{t(`claude.status_${token.status}`)}</Pill>
                      </td>
                      <td>{expiryText(token)}</td>
                      <td>{token.last_used_at ? fmtDateTime(token.last_used_at) : <span className="nt-muted">{t('claude.neverUsed')}</span>}</td>
                      <td>
                        <button type="button" className="cl-usage-btn" onClick={() => toggleUsage(token)} title={t('claude.usageShow')}>
                          <strong>{token.usage.today}</strong> <span className="nt-muted">{t('claude.usageToday')}</span>
                          <span className="nt-muted"> · {token.usage.last7} {t('claude.usage7')}</span>
                          {token.usage.errors7 > 0 && <span className="cl-usage-errors"> · {token.usage.errors7} {t('claude.usageErrors')}</span>}
                        </button>
                        {usageOpen[token.id] && <UsageBars days={usageOpen[token.id]} t={t} />}
                      </td>
                      <td>
                        {token.status !== 'revoked' && (
                          <div className="cl-actions">
                            <Select value="" onChange={(e) => handleExpiry(token, e.target.value)} aria-label={t('claude.changeExpiry')}>
                              <option value="">{t('claude.changeExpiry')}</option>
                              {EXPIRY_CHOICES.map((d) => (
                                <option key={d} value={d}>{t(`claude.exp_${d}`)}</option>
                              ))}
                            </Select>
                            <button className="btn btn-secondary btn-sm cl-revoke" type="button" onClick={() => handleRevoke(token)} title={t('claude.revokeTitle')}>
                              {t('claude.revoke')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {canManageAll && (
          <section className="cl-card">
            <h3 className="cl-card-title">{t('claude.settingsTitle')}</h3>
            <form className="cl-new" onSubmit={handleSaveLimit}>
              <div className="form-group cl-new-expiry">
                <label>{t('claude.rateLimit')}</label>
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={rateLimit}
                  placeholder={t('claude.rateLimitPlaceholder')}
                  onChange={(e) => setRateLimit(e.target.value)}
                />
              </div>
              <button type="submit" className="btn" disabled={savingLimit}>
                {savingLimit ? t('common.saving') : t('common.save')}
              </button>
            </form>
            <p className="tp-hint">{t('claude.rateLimitHint')}</p>
          </section>
        )}
      </div>
    </Layout>
  );
}

export default ClaudeConnector;
